import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { updateHouseholdAPI } from "../../../api/updateHouseholdAPI";
import { formatAccountName } from "../../../utils/formatters";
import { useConfirm } from "../../../context/ConfirmContext";
import clientAxiosInstance from "../../../api/clientAxiosInstance";

import ClientPortalLayout from "../../ClientPortalLayout";
import PendingDataOverview from "./PendingDataOverview";
import PendingDataDetail from "./PendingDataDetail";
import {
  isBankLinkedToAccount,
  resolveAccountBankIssues,
  getMissingBankAccountsMap,
  filterBankIssuesByReason,
} from "../../../utils/bankAccounts";
import { getUnmappedMembers } from "../../../utils/memberLinks";
import { getBrokerageFlags, showIdInfo } from "../../../utils/brokerageFields";

// ── Form component imports (stubs until implemented) ──────────────────────────
import IncomeDetailsForm from "./HouseholdFields/IncomeDetailsForm";
import InvestmentExperienceForm from "./HouseholdFields/InvestmentExperienceForm";
import InvestmentObjectiveForm from "./HouseholdFields/InvestmentObjectiveForm";
import PersonalDetailsForm from "./MemberFields/PersonalDetailsForm";
import EmploymentInfoForm from "./MemberFields/EmploymentInfoForm";
import IdDetailsForm from "./MemberFields/IdDetailsForm";
import UnlinkedMembersForm from "./MemberFields/UnlinkedMembersForm";
import BasicDetailsForm from "./AccountFields/BasicDetailsForm";
import RiskToleranceForm from "./AccountFields/RiskToleranceForm";
import TransferInfoForm from "./AccountFields/TransferInfoForm";
import TrustInfoForm from "./AccountFields/TrustInfoForm";
import BankDetailsForm from "./AccountFields/BankDetailsForm";
import WithdrawalsForm from "./AccountFields/WithdrawalsForm";
import DepositsForm from "./AccountFields/DepositsForm";
import BeneficiariesForm from "./AccountFields/BeneficiariesForm";
import TrustedContactsForm from "./AccountFields/TrustedContactsForm";
import UnlinkedBankAccountsForm from "./AccountFields/UnlinkedBankAccountsForm";
import BusinessInfoForm from "./AccountFields/BusinessInfoForm";
import InheritanceInfoForm from "./AccountFields/InheritanceInfoForm";
import MinorDetailsForm from "./AccountFields/MinorDetailsForm";

// ── Constants ─────────────────────────────────────────────────────────────────

const INCOME_FIELD_KEYS = [
  "tax_info",
  "estimated_liquid_net_worth",
  "estimated_total_net_worth",
  "annual_income",
  "aum",
  "annual_expense_range",
  "special_expense_range",
];

const HOUSEHOLD_FIELD_MAP = {
  income_details: { label: "Income Details", component: IncomeDetailsForm },
  investor_experience: { label: "Investment Experience", component: InvestmentExperienceForm },
  investor_associations: { label: "Investment Objective", component: InvestmentObjectiveForm },
};

const MEMBER_FIELD_MAP = {
  personal_info: {
    key: "personal_info",
    label: "Personal Details",
    component: PersonalDetailsForm,
    section: "personal",
  },
  is_deceased: {
    key: "is_deceased",
    label: "Personal Details",
    component: PersonalDetailsForm,
    section: "personal",
  },
  date_of_death: {
    key: "date_of_death",
    label: "Personal Details",
    component: PersonalDetailsForm,
    section: "personal",
  },
  address: {
    key: "address",
    label: "Address",
    component: PersonalDetailsForm,
    section: "address",
  },
  employment_info: {
    key: "employment_info",
    label: "Employment Details",
    component: EmploymentInfoForm,
  },
  id_info: {
    key: "id_info",
    label: "ID Details",
    component: IdDetailsForm,
  },
};

const CANONICAL_MEMBER_FIELD_KEY = {
  is_deceased: "personal_info",
  date_of_death: "personal_info",
};

const HIDDEN_ACCOUNT_FIELDS = new Set(["risk_tolerance"]);

const ACCOUNT_FIELD_MAP = {
  basic_details: { key: "basic_details", label: "Basic Details", component: BasicDetailsForm },
  transfer_info: { key: "transfer_info", label: "Transfer Details", component: TransferInfoForm },
  trust_info: { key: "trust_info", label: "Trust Info", component: TrustInfoForm },
  bank_details: { key: "bank_details", label: "Bank Details", component: BankDetailsForm },
  withdrawals: { key: "withdrawals", label: "Withdrawals", component: WithdrawalsForm },
  deposits: { key: "deposits", label: "Deposits", component: DepositsForm },
  beneficiaries: { key: "beneficiaries", label: "Beneficiaries", component: BeneficiariesForm },
  trusted_contacts: { key: "trusted_contacts", label: "Trusted Contacts", component: TrustedContactsForm },
  business_info: { key: "business_info", label: "Business Details", component: BusinessInfoForm },
  inheritance_info: { key: "inheritance_info", label: "Decedent Details", component: InheritanceInfoForm },
  minor_details: { key: "minor_details", label: "Minor Details", component: MinorDetailsForm },
};

const CATEGORY_ORDER = ["household", "personal", "accounts"];

/**
 * Backend `missing_fields.accounts[accountId]` is a flat array of account-level
 * field keys (bank problems now live in the top-level `missing_fields.bank_accounts`
 * map, not here). Object shape `{ fields: [...] }` is still tolerated.
 */
function normalizeAccountMissingEntry(rawEntry) {
  if (!rawEntry) return [];
  if (Array.isArray(rawEntry)) {
    return rawEntry.filter((entry) => typeof entry === "string");
  }
  if (typeof rawEntry === "object" && Array.isArray(rawEntry.fields)) {
    return rawEntry.fields.filter((f) => typeof f === "string");
  }
  return [];
}

// ── Sub-components ────────────────────────────────────────────────────────────

const SkipToReviewButton = ({ onClick, isAllSectionsComplete }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
  >
    {isAllSectionsComplete
      ? "Review & Submit"
      : "Skip to Review & Submit"}
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

const ErrorBanner = ({ message }) => (
  <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      />
    </svg>
    <span>{message}</span>
  </div>
);

// ── PendingDataV2 ─────────────────────────────────────────────────────────────

const PendingDataV2 = ({
  householdData,
  household_id,
  clientToken,
  onDataUpdate,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { household_id: householdIdFromRoute } = useParams();
  const actualHouseholdId = household_id || householdIdFromRoute;
  const confirmDialogFn = useConfirm();

  // ── View state ──────────────────────────────────────────────────────────────
  const [view, setView] = useState("overview"); // "overview" | "detail"
  const [activeCategory, setActiveCategory] = useState("household");
  const [activeEntityIndex, setActiveEntityIndex] = useState(0);
  const [activeSubTabIndex, setActiveSubTabIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [clearingAdvisorNote, setClearingAdvisorNote] = useState(false);

  // ── Derived pending data ────────────────────────────────────────────────────

  const missingFields = useMemo(
    () => householdData?.missing_fields || {},
    [householdData]
  );

  const brokerageFlags = useMemo(
    () => getBrokerageFlags(householdData),
    [householdData]
  );
  const isAllSectionsComplete = useMemo(() => {
    const householdMissing = Array.isArray(missingFields?.household)
      ? missingFields.household
      : [];
    const memberMissing = missingFields?.household_members || {};
    const accountMissing = missingFields?.accounts || {};

    // Filter out hidden fields from account completeness check
    const hasVisibleAccountMissing = Object.entries(accountMissing).some(
      ([, fields]) =>
        Array.isArray(fields) && fields.some((f) => !HIDDEN_ACCOUNT_FIELDS.has(f)),
    );

    // A bank account with missing field data keeps the client's work incomplete.
    // missing_doc is advisor-only and does not gate client completeness.
    const hasBankDataMissing = Object.values(
      getMissingBankAccountsMap(missingFields),
    ).some((v) => Array.isArray(v?.reasons) && v.reasons.includes("missing_data"));

    return (
      householdMissing.length === 0 &&
      Object.keys(memberMissing).length === 0 &&
      !hasVisibleAccountMissing &&
      !hasBankDataMissing
    );
  }, [missingFields]);

  const members = useMemo(
    () => householdData?.members || [],
    [householdData]
  );

  const accounts = useMemo(
    () => householdData?.accounts || [],
    [householdData]
  );

  // Household-level pending items
  const householdPending = useMemo(() => {
    const rawFields = missingFields.household || [];
    const result = [];

    // Income Details: triggered by any INCOME_FIELD_KEYS or "income_details" itself
    const hasIncomeFields =
      rawFields.includes("income_details") ||
      rawFields.some((f) => INCOME_FIELD_KEYS.includes(f));

    if (hasIncomeFields) {
      result.push({ ...HOUSEHOLD_FIELD_MAP.income_details, key: "income_details" });
    }

    if (rawFields.includes("investor_experience")) {
      result.push({ ...HOUSEHOLD_FIELD_MAP.investor_experience, key: "investor_experience" });
    }

    if (rawFields.includes("investor_associations")) {
      result.push({ ...HOUSEHOLD_FIELD_MAP.investor_associations, key: "investor_associations" });
    }

    return result;
  }, [missingFields]);

  // Unmapped household members (not linked to any account in any role)
  const unmappedMembers = useMemo(
    () => getUnmappedMembers(members, accounts),
    [members, accounts],
  );

  // Member-level pending items
  const membersPending = useMemo(() => {
    const rawMembers = missingFields.household_members || {};

    const pendingList = Object.entries(rawMembers)
      .filter(([, fields]) => Array.isArray(fields) && fields.length > 0)
      .map(([memberId, fields]) => {
        const memberObj = members.find((m) => String(m.id) === String(memberId));
        const personalInfo =
          typeof memberObj?.personal_info === "object"
            ? memberObj.personal_info
            : (() => {
                try {
                  return JSON.parse(memberObj?.personal_info || "{}");
                } catch {
                  return {};
                }
              })();

        const firstName = personalInfo?.first_name || "";
        const lastName = personalInfo?.last_name || "";
        const memberName =
          firstName || lastName
            ? `${firstName} ${lastName}`.trim()
            : `Member ${memberId}`;

        const mappedFields = fields
          .filter((f) => MEMBER_FIELD_MAP[f])
          .filter((f) => f !== "id_info" || showIdInfo(brokerageFlags))
          .map((f) => ({
            ...MEMBER_FIELD_MAP[f],
            key: CANONICAL_MEMBER_FIELD_KEY[f] || f,
          }));

        // Dedupe by label+section so personal_info / is_deceased / date_of_death
        // (all rendered by the same Personal Details form) collapse into one item.
        const seenLabelSection = new Set();
        const dedupedFields = mappedFields.filter((f) => {
          const dedupeKey = `${f.label}|${f.section || ""}`;
          if (seenLabelSection.has(dedupeKey)) return false;
          seenLabelSection.add(dedupeKey);
          return true;
        });

        return { memberId, memberName, memberObj, fields: dedupedFields };
      })
      .filter((entry) => entry.fields.length > 0);

    // Add unlinked members as a virtual entity at the end
    if (unmappedMembers.length > 0) {
      pendingList.push({
        memberId: "__unlinked_members__",
        memberName: "Unlinked Members",
        memberObj: null,
        fields: [
          {
            key: "unlinked_members",
            label: "Unlinked Members",
            component: UnlinkedMembersForm,
          },
        ],
      });
    }

    return pendingList;
  }, [missingFields, members, unmappedMembers, brokerageFlags]);

  // Unmapped bank accounts
  const unmappedBankAccounts = useMemo(() => {
    const bankAccounts = householdData?.bank_accounts || [];
    if (!bankAccounts.length || !accounts.length) return [];
    return bankAccounts.filter((ba) => {
      const isLinked = accounts.some((account) => {
        const inLinks = isBankLinkedToAccount(account, ba.id);
        const inWithdrawals = account.withdrawals?.some(
          (w) => w.destination_identifier === ba.id,
        );
        return inLinks || inWithdrawals;
      });
      return !isLinked;
    });
  }, [householdData, accounts]);

  // Account-level pending items
  const accountsPending = useMemo(() => {
    const rawAccounts = missingFields.accounts || {};

    // Iterate all accounts: a bank-only issue now lives in the top-level
    // bank_accounts map, so the account may not appear under missing_fields.accounts.
    const pendingList = accounts
      .map((accountObj, idx) => {
        const accountId = accountObj.id;
        const fieldKeys = normalizeAccountMissingEntry(rawAccounts[accountId]);

        // The client acts on field data only (missing_doc is advisor-only).
        const incompleteBanks = filterBankIssuesByReason(
          resolveAccountBankIssues(accountObj, missingFields, householdData),
          "missing_data",
        );

        const mappedFields = fieldKeys
          .filter((f) => ACCOUNT_FIELD_MAP[f])
          .map((f) => ({ ...ACCOUNT_FIELD_MAP[f], key: f }));

        if (incompleteBanks.length > 0 && !mappedFields.some((f) => f.key === "bank_details")) {
          mappedFields.push({ ...ACCOUNT_FIELD_MAP.bank_details, key: "bank_details" });
        }

        if (mappedFields.length === 0) return null;

        const accountName = formatAccountName(accountObj, idx + 1, members);

        return { accountId, accountName, accountObj, incompleteBanks, fields: mappedFields };
      })
      .filter(Boolean);

    // Add unlinked bank accounts as a virtual entity at the end
    if (unmappedBankAccounts.length > 0) {
      pendingList.push({
        accountId: "__unlinked_bank_accounts__",
        accountName: "Unlinked Bank Accounts",
        accountObj: null,
        fields: [
          {
            key: "unlinked_bank_accounts",
            label: "Unlinked Bank Accounts",
            component: UnlinkedBankAccountsForm,
          },
        ],
      });
    }

    return pendingList;
  }, [missingFields, accounts, members, unmappedBankAccounts, householdData]);

  // ── Pending list by category helper ────────────────────────────────────────

  const getPendingList = useCallback(
    (category) => {
      if (category === "household") return householdPending;
      if (category === "personal") return membersPending;
      if (category === "accounts") return accountsPending;
      return [];
    },
    [householdPending, membersPending, accountsPending]
  );

  // After save, missing_fields can shrink (e.g. trust_info drops off) while activeSubTabIndex
  // still points at the old tab — no form matches and the card looks empty. Clamp synchronously.
  useLayoutEffect(() => {
    if (view !== "detail") return;
    const pendingList = getPendingList(activeCategory);
    let len = 0;
    if (activeCategory === "household") {
      len = pendingList.length;
    } else {
      const ent = pendingList[activeEntityIndex];
      len = ent?.fields?.length ?? 0;
    }
    if (len <= 0) return;
    setActiveSubTabIndex((prev) =>
      prev >= len ? Math.max(0, len - 1) : prev,
    );
  }, [
    view,
    activeCategory,
    activeEntityIndex,
    householdData,
    householdPending,
    membersPending,
    accountsPending,
    getPendingList,
  ]);

  // ── Auto-advance useEffect ──────────────────────────────────────────────────
  // Normalises out-of-bounds indices and auto-advances the linear flow.

  useEffect(() => {
    if (view !== "detail") return;

    const pendingList = getPendingList(activeCategory);

    // Find next category with pending items, wrapping around to the beginning
    const findNextCategory = () => {
      const currentIdx = CATEGORY_ORDER.indexOf(activeCategory);
      // First check categories after current
      const after = CATEGORY_ORDER.slice(currentIdx + 1).find(
        (cat) => getPendingList(cat).length > 0
      );
      if (after) return after;
      // Wrap around: check from the beginning up to (and including) current
      const wrapped = CATEGORY_ORDER.slice(0, currentIdx + 1).find(
        (cat) => getPendingList(cat).length > 0
      );
      return wrapped || null;
    };

    if (activeCategory === "household") {
      // Household uses sub-tabs only (no entity dimension)
      if (activeSubTabIndex >= pendingList.length) {
        const nextCategory = findNextCategory();
        if (nextCategory) {
          setActiveCategory(nextCategory);
          setActiveEntityIndex(0);
          setActiveSubTabIndex(0);
        } else {
          setView("overview");
        }
      }
      return;
    }

    // personal / accounts — entity + sub-tab dimensions
    const entity = pendingList[activeEntityIndex];

    if (!entity) {
      const nextCategory = findNextCategory();
      if (nextCategory) {
        setActiveCategory(nextCategory);
        setActiveEntityIndex(0);
        setActiveSubTabIndex(0);
      } else {
        setView("overview");
      }
      return;
    }

    if (activeSubTabIndex >= entity.fields.length) {
      // Sub-tab out of bounds for this entity — advance to next entity
      const nextEntityIndex = activeEntityIndex + 1;
      if (nextEntityIndex < pendingList.length) {
        setActiveEntityIndex(nextEntityIndex);
        setActiveSubTabIndex(0);
      } else {
        const nextCategory = findNextCategory();
        if (nextCategory) {
          setActiveCategory(nextCategory);
          setActiveEntityIndex(0);
          setActiveSubTabIndex(0);
        } else {
          setView("overview");
        }
      }
    }
  }, [view, activeCategory, activeEntityIndex, activeSubTabIndex, getPendingList]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSaveAndNext = useCallback(
    async (payload) => {
      setSaving(true);
      setError("");

      const result = await updateHouseholdAPI(actualHouseholdId, clientToken, payload);

      if (!result.success) {
        setError(result.error || "Failed to save. Please try again.");
        setSaving(false);
        return;
      }

      if (result.data) {
        onDataUpdate(result.data);
      }
      setActiveSubTabIndex((prev) => prev + 1);
      setSaving(false);
    },
    [actualHouseholdId, clientToken, onDataUpdate]
  );

  const handleSaveAndExit = useCallback(
    async (payload) => {
      setSaving(true);
      setError("");

      const result = await updateHouseholdAPI(actualHouseholdId, clientToken, payload);

      if (!result.success) {
        setError(result.error || "Failed to save. Please try again.");
        setSaving(false);
        return;
      }

      if (result.data) {
        onDataUpdate(result.data);
      }
      setView("overview");
      setSaving(false);
    },
    [actualHouseholdId, clientToken, onDataUpdate]
  );

  const handleSkipToReview = useCallback(() => {
    navigate(`/household/${actualHouseholdId}/full-review`);
  }, [navigate, actualHouseholdId]);

  const handleBackToDocumentUpload = useCallback(() => {
    navigate(`/household/${actualHouseholdId}/document-upload`);
  }, [navigate, actualHouseholdId]);

  // Called from Overview cards — start filling a category
  const handleComplete = useCallback((category) => {
    setView("detail");
    setActiveCategory(category);
    setActiveEntityIndex(0);
    setActiveSubTabIndex(0);
  }, []);

  const handleBackToOverview = useCallback(() => {
    setView("overview");
  }, []);

  const handleClearAdvisorNote = useCallback(async () => {
    if (!actualHouseholdId || !clientToken || clearingAdvisorNote) return;
    setClearingAdvisorNote(true);
    try {
      await clientAxiosInstance.delete(
        `/client/household/${actualHouseholdId}/advisor-note`,
        {
          headers: {
            Authorization: `Bearer ${clientToken}`,
          },
        },
      );
      if (onDataUpdate) onDataUpdate();
    } catch (e) {
      console.error("Failed to clear advisor note:", e);
    } finally {
      setClearingAdvisorNote(false);
    }
  }, [actualHouseholdId, clientToken, clearingAdvisorNote, onDataUpdate]);

  const confirmClearAdvisorNote = useCallback(async () => {
    if (!householdData?.advisor_note || householdData.advisor_note.trim() === "")
      return;
    const ok = await confirmDialogFn({
      title: "Delete advisor note?",
      message: "This note will be removed and cannot be shown again.",
      confirmLabel: "Delete",
    });
    if (ok) handleClearAdvisorNote();
  }, [householdData?.advisor_note, handleClearAdvisorNote, confirmDialogFn]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <ClientPortalLayout
      householdData={householdData}
      currentStep="pending-data"
      title="Complete pending items"
      subtitle="We've pre-filled what your advisor provided. Please confirm any remaining fields."
      headerAction={
        <SkipToReviewButton
          onClick={handleSkipToReview}
          isAllSectionsComplete={isAllSectionsComplete}
        />
      }
      backLink={view === "overview" ? handleBackToDocumentUpload : null}
      backLinkText={view === "overview" ? "Back to Document Upload" : null}
      demoVideoUrl={process.env.REACT_APP_DEMO_VIDEO_URL || ""}
    >
      {error && <ErrorBanner message={error} />}

      {householdData?.advisor_note &&
        householdData.advisor_note.trim() !== "" && (
          <div className="mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h8M8 14h6m-2 7H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v8l-4 4z"
                />
              </svg>
              <div>
                <p className="text-xs font-semibold text-emerald-800">
                  Message from your advisor
                </p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {householdData.advisor_note}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={confirmClearAdvisorNote}
              disabled={clearingAdvisorNote}
              className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {clearingAdvisorNote ? "Deleting..." : "Delete"}
            </button>
          </div>
        )}

      {view === "overview" ? (
        <PendingDataOverview
          householdPending={householdPending}
          membersPending={membersPending}
          accountsPending={accountsPending}
          onComplete={handleComplete}
        />
      ) : (
        <PendingDataDetail
          householdData={householdData}
          household_id={actualHouseholdId}
          clientToken={clientToken}
          householdPending={householdPending}
          membersPending={membersPending}
          accountsPending={accountsPending}
          activeCategory={activeCategory}
          activeEntityIndex={activeEntityIndex}
          activeSubTabIndex={activeSubTabIndex}
          saving={saving}
          onCategoryChange={(cat) => {
            setActiveCategory(cat);
            setActiveEntityIndex(0);
            setActiveSubTabIndex(0);
          }}
          onEntityTabChange={(i) => {
            setActiveEntityIndex(i);
            setActiveSubTabIndex(0);
          }}
          onSubTabChange={(i) => setActiveSubTabIndex(i)}
          onSaveAndNext={handleSaveAndNext}
          onSaveAndExit={handleSaveAndExit}
          onBackToOverview={handleBackToOverview}
          onSkipToReview={handleSkipToReview}
          onLogout={onLogout}
          onDataUpdate={onDataUpdate}
        />
      )}
    </ClientPortalLayout>
  );
};

export default PendingDataV2;
